import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ColumnDef } from '@tanstack/react-table';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { TanTable } from '../TanTable';
import {
  useAdditionalFieldColumns,
  type AdditionalFieldSelection,
} from '../../hooks/useAdditionalFields';
import type { BulkEditMeta } from '../TanTable';
import DateTimePicker from '../DateTimePicker';
import TypeAhead, { TypeAheadOption } from '../TypeAhead';
import ProDetailSlideOver from '../pro/ProDetailSlideOver';
import TroubleTicketEditModal from '../../pages/pro/components/TroubleTicketEditModal';
import { Tooltip } from '../ui/Tooltip';
import ModalHeader from '../ui/ModalHeader';
import { useModalDrag } from '../../hooks/useModalDrag';
import { config } from '../../config/config';
import { useToast } from '../../contexts/ToastContext';
import { ViewedProsProvider } from '../../contexts/ViewedProsContext';
import {
  TroubleTicketSaveProvider,
  type TroubleTicketSaveResult,
} from '../../contexts/TroubleTicketSaveContext';
import { useVisibilityAwareInterval } from '../../hooks/useVisibilityAwareInterval';
import schedulingService from '../../services/scheduling.service';
import troubleTicketsService, {
  type TroubleTicket,
  type TroubleTicketUpdatePayload,
} from '../../services/troubleTickets.service';
import repairOrdersService from '../../services/repairOrders.service';
import { LcResolutionItem } from '../../types/scheduling';
import { DriverLookup } from '../../types/repairOrders';
import {
  SERVICE_FAILURE_TYPE_OPTIONS,
  serviceFailureTypeMissing,
} from '../../constants/serviceFailureTypes';

// Trouble-ticket fields edit the troubleticket record; reschedule fields edit
// the reschedule record. A combined row may carry one or both.
// `resolve` is staged (and saved) but has no column — it's captured by the
// resolve modal when a ticket is set to RESOLVED.
type TtField = 'tt_status' | 'assignedto' | 'preventable' | 'driverticket' | 'servicefailure' | 'resolve';
type RsField = 'servicefailuredate' | 'reason' | 'servicefailuretype' | 'thd_reason' | 'responsibledriver' | 'comments';
// The Reviewed checkbox (report page) writes EVERY record the row carries — the
// ticket alone, the service failure alone, or both together — so it belongs to
// neither set. It is a write-only Y/N toggle: there is no flag column, the
// backend stamps / clears reviewedby + revieweddatetime from it.
type BothField = 'reviewed';
type EditableField = TtField | RsField | BothField;
const TT_FIELDS = new Set<string>(['tt_status', 'assignedto', 'preventable', 'driverticket', 'servicefailure', 'resolve']);
const RS_FIELDS = new Set<string>(['servicefailuredate', 'reason', 'servicefailuretype', 'thd_reason', 'responsibledriver', 'comments']);
const BOTH_FIELDS = new Set<string>(['reviewed']);

// Staged edits, keyed by composite row id (a row maps to up to two records).
type RowEdits = Record<string, Partial<Record<EditableField, string | null>>>;

export const STATUS_OPTIONS = [
  { value: 'NEW', label: 'New' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

// Mirror the TroubleTicketEditModal's Assigned To options (Whse CSR retired).
const ASSIGNED_TO_OPTIONS = [
  { value: 'CSR', label: 'CSR' },
  { value: 'DISPATCHER', label: 'Dispatcher' },
];

/** This board's own column ids -> the registry fields they already render, so the
 *  picker must not offer them a second time and each column can take the registry's
 *  title. `container` is here for the title; its id matches the fieldname, so the
 *  picker's name rule would suppress it either way. */
const LC_RESOLUTION_COVERED_FIELDS = {
  customer: ['customername'],
  container: ['container'],
  pro_bol: ['bol'],
} as const;

/** Stable, unique id for a combined row. Includes pronumber because the same
 * ticket (with no linked reschedule) can surface against more than one order —
 * matched by pronumber in one UNION branch and by bookingnumber in another —
 * so ticket+reschedule recnos alone are not unique. */
export const rowKey = (row: LcResolutionItem): string =>
  `${row.pronumber}|tt${row.troubleticketrecno ?? ''}|rs${row.reschedulerecno ?? ''}`;

const isoDateOnly = (iso: string | null | undefined): string => {
  if (!iso) return '';
  return iso.length >= 10 ? iso.slice(0, 10) : '';
};

/** Pull a readable message from an axios error. FastAPI returns `detail` as a
 * string for our HTTPExceptions but as an array of {msg,...} for Pydantic
 * validation errors — handle both so we never render "[object Object]". */
const errorMessage = (reason: any): string => {
  const detail = reason?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => e?.msg || 'invalid').join('; ') || 'validation error';
  }
  return reason?.message || 'save failed';
};

/**
 * Free-text cell that keeps keystrokes local and commits on blur, so typing
 * doesn't rebuild the whole column array per keypress. `incoming` resyncs the
 * input when the external value changes — bulk edit, discard, or a refresh.
 */
const TextCell: React.FC<{
  incoming: string;
  className: string;
  maxLength?: number;
  onCommit: (value: string) => void;
}> = ({ incoming, className, maxLength, onCommit }) => {
  const [val, setVal] = useState(incoming);
  useEffect(() => { setVal(incoming); }, [incoming]);
  return (
    <input
      type="text"
      value={val}
      maxLength={maxLength}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => { if (val !== incoming) onCommit(val); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      className={className}
    />
  );
};

/** The stored value for an editable field, before any staged edit. */
const originalValue = (row: LcResolutionItem, col: EditableField): string => {
  if (col === 'servicefailuredate') return isoDateOnly(row.servicefailuredate);
  if (col === 'tt_status') return row.tt_status ?? '';
  // Rolled up by the backend across the row's records; unreviewed reads as 'N'.
  if (col === 'reviewed') return row.reviewed === 'Y' ? 'Y' : 'N';
  return (row[col] as string | null) ?? '';
};

/** Apply staged edits to a row so the cell shows the pending value. */
const effective = (row: LcResolutionItem, edits: RowEdits, col: EditableField): string => {
  const staged = edits[rowKey(row)]?.[col];
  if (staged !== undefined) return staged ?? '';
  return originalValue(row, col);
};

/**
 * Stage one cell edit. A value equal to the STORED one is not a pending change:
 * it is dropped, and the row leaves the pending set once nothing else is staged
 * — so un-check → re-check, or retyping the original text, leaves the row clean
 * and out of the Save count instead of "dirty" with nothing to write.
 */
const stageEdit = (
  prev: RowEdits,
  row: LcResolutionItem,
  col: EditableField,
  value: string | null,
): RowEdits => {
  const key = rowKey(row);
  const rowEdits = { ...(prev[key] || {}) };
  if ((value ?? '') === originalValue(row, col)) delete rowEdits[col];
  else rowEdits[col] = value;
  if (Object.keys(rowEdits).length === 0) {
    if (!(key in prev)) return prev;
    const rest = { ...prev };
    delete rest[key];
    return rest;
  }
  return { ...prev, [key]: rowEdits };
};

/**
 * Captures the resolution text when a ticket is set to RESOLVED (single or, via
 * bulk, several at once — the text applies to all). Preventable is also required
 * to resolve but is set via its own column and validated per-row at save, not
 * gated here.
 */
const ResolveTicketModal: React.FC<{
  count: number;
  initialResolve: string;
  onCancel: () => void;
  onConfirm: (resolve: string) => void;
}> = ({ count, initialResolve, onCancel, onConfirm }) => {
  const [resolveText, setResolveText] = useState(initialResolve);
  const [error, setError] = useState<string | null>(null);
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(true);

  const submit = () => {
    if (!resolveText.trim()) {
      setError('Resolution text is required to resolve.');
      return;
    }
    onConfirm(resolveText.trim());
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-scrim/40"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-panel rounded-lg shadow-2xl w-full max-w-md mx-4 border border-slate-200 dark:border-gray-700"
      >
        <ModalHeader
          title={count > 1 ? `Resolve ${count} Trouble Tickets` : 'Resolve Trouble Ticket'}
          onClose={onCancel}
          dragHandleProps={dragHandleProps}
        />
        <div className="px-5 py-4 space-y-4">
          {error && (
            <div className="p-2 rounded bg-red-50 border border-red-200 text-sm text-red-700 dark:bg-red-900/30 dark:border-red-800 dark:text-red-300">
              {error}
            </div>
          )}
          {count > 1 && (
            <p className="text-xs text-muted">
              This resolution applies to all {count} selected tickets.
            </p>
          )}
          <div>
            <label className="block text-xs font-medium text-muted mb-1">
              Resolution<span className="text-red-500 ml-1">*</span>
            </label>
            <textarea
              rows={4}
              value={resolveText}
              onChange={(e) => setResolveText(e.target.value)}
              autoFocus
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm font-medium text-label bg-panel border border-slate-300 rounded hover:bg-slate-50 dark:border-gray-600 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded hover:bg-blue-700"
          >
            Resolve
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export interface LcResolutionBoardProps {
  /** Combined trouble-ticket + reschedule rows to display. */
  rows: LcResolutionItem[];
  loading: boolean;
  /**
   * Reload the rows. Called after a save or a ticket-modal save (foreground),
   * and on the auto-refresh tick ({ background: true }) so the parent can skip
   * the loading flash / error toast for a silent refresh.
   */
  onRefetch: (opts?: { background?: boolean }) => void;
  title: string;
  subtitle: string;
  /** TanTable settings key — distinct per page so column prefs don't collide. */
  settingsKey: string;
  /** Page-specific filter controls rendered above the table. */
  filterBar?: React.ReactNode;
  /** Background refresh cadence in ms; omit/null to disable (e.g. report page). */
  autoRefreshMs?: number | null;
  /** Highlight the Driver Ticket cell red when Y (operational page only). */
  highlightDriverTicketY?: boolean;
  /** Show a read-only Resolution column after Issue (report page only — the
   *  operational queue is open tickets, where resolution is empty). */
  showResolveColumn?: boolean;
  /** Show the Reviewed checkbox + Reviewed By / Reviewed Date/Time columns
   *  (report page only — a manager's pass over coded work, not part of the LC
   *  queue). One checkbox per row writes the ticket and/or service failure the
   *  row carries; the backend stamps who/when. */
  showReviewColumns?: boolean;
  /** The page's added-order-column selection. Owned by the PARENT because it is an
   *  input to the fetch, which the parent runs; the board only turns it into columns
   *  and hands the picker back. Keyed on the same settingsKey. */
  fieldSelection: AdditionalFieldSelection;
  /** One sentence naming what this page's query leaves out, shown when a user pastes a
   *  list into a column filter. The two pages scope their fetch very differently — the
   *  queue is active tickets for the selected LCs, the report is a date range — and
   *  "14 not in this view" means nothing without knowing which. Supplied by the parent
   *  because the parent owns the fetch. */
  listFilterScopeNote?: string;
}

/**
 * Shared TTs & Service Failures board: the editable table, staged-edit + save
 * pipeline, bulk edit, and the order / trouble-ticket / resolve modals. Both
 * the operational scheduling page and the Researching/Reports variant render
 * this, supplying their own fetch + filter bar (and the small behavioural
 * differences via props).
 */
const LcResolutionBoardContent: React.FC<LcResolutionBoardProps> = ({
  rows,
  loading,
  listFilterScopeNote,
  onRefetch,
  title,
  subtitle,
  settingsKey,
  filterBar,
  autoRefreshMs = null,
  highlightDriverTicketY = false,
  showResolveColumn = false,
  showReviewColumns = false,
  fieldSelection,
}) => {
  const { showToast } = useToast();

  const [edits, setEdits] = useState<RowEdits>({});
  const [saving, setSaving] = useState(false);

  // ── Dropdown options for editable columns ────────────────────────────────
  const [reasonOptions, setReasonOptions] = useState<string[]>([]);
  const [thdReasonOptions, setThdReasonOptions] = useState<string[]>([]);
  const [drivers, setDrivers] = useState<DriverLookup[]>([]);
  useEffect(() => {
    const controller = new AbortController();
    schedulingService
      .listRescheduleReasons(controller.signal)
      .then((list) => { if (!controller.signal.aborted) setReasonOptions(list); })
      .catch(() => { /* leave empty */ });
    troubleTicketsService
      .listThdReasons(controller.signal)
      .then((list) => { if (!controller.signal.aborted) setThdReasonOptions(list); })
      .catch(() => { /* leave empty */ });
    // Driver lookup for the Responsible Driver column — same source as the
    // Repair Orders "Reported By" picker and the reschedule edit modal.
    repairOrdersService
      .getDrivers()
      .then((res) => { if (!controller.signal.aborted) setDrivers(res.data || []); })
      .catch(() => { /* leave empty */ });
    return () => controller.abort();
  }, []);

  // Driver options keyed by unit number — that's what we store in
  // responsibledriver. Drivers without a unit number can't be saved as one.
  const driverOptions = useMemo<TypeAheadOption[]>(
    () =>
      drivers
        .filter((d) => d.unitnumber)
        .map((d) => {
          const toTitleCase = (s: string) =>
            s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
          const name = toTitleCase(`${d.firstname} ${d.lastname}`.trim());
          return {
            value: d.unitnumber,
            label: name ? `${d.unitnumber} - ${name}` : d.unitnumber,
            description: d.active ? undefined : '⚠ Inactive',
            group: d.active ? 'Active' : 'Inactive',
          };
        }),
    [drivers],
  );
  // Plain {value,label} list for the bulk-edit dropdown.
  const driverBulkOptions = useMemo(
    () => driverOptions.map((o) => ({ value: o.value, label: o.label })),
    [driverOptions],
  );

  // ── Drawer state ─────────────────────────────────────────────────────────
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedPro, setSelectedPro] = useState<number | null>(null);

  const openOrderDrawer = useCallback((pronumber: number) => {
    setSelectedPro(pronumber);
    setIsDrawerOpen(true);
  }, []);
  const closeOrderDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setSelectedPro(null);
  }, []);

  // ── Trouble-ticket drawer ────────────────────────────────────────────────
  // Opened from the clickable Ticket # column. Mirrors the Dispatch Dashboard:
  // fetch the full ticket by recno, then open the shared editor as a drawer.
  const [selectedTicket, setSelectedTicket] = useState<TroubleTicket | null>(null);
  const [ticketLoading, setTicketLoading] = useState(false);

  const openTicketDrawer = useCallback(async (recno: number) => {
    setTicketLoading(true);
    setSelectedTicket(null);
    try {
      const ticket = await troubleTicketsService.getByRecno(recno);
      setSelectedTicket(ticket);
    } catch (err) {
      console.error('Failed to fetch ticket:', err);
      showToast({ type: 'error', message: 'Failed to load trouble ticket' });
    } finally {
      setTicketLoading(false);
    }
  }, [showToast]);

  // ── Edit helpers ─────────────────────────────────────────────────────────
  // Resolve modal: open with the row keys being resolved + seed values. Held
  // here so both single (inline status → RESOLVED) and bulk paths share it.
  const [resolveModal, setResolveModal] = useState<{
    rowKeys: string[];
    initResolve: string;
  } | null>(null);

  // Read through a ref so setRowEdit keeps a stable identity (it is a dependency
  // of the column definitions). Populated right after rowsByKey is built below.
  const rowsByKeyRef = useRef<Record<string, LcResolutionItem>>({});
  const setRowEdit = useCallback((key: string, col: EditableField, value: string | null) => {
    setEdits((prev) => {
      const row = rowsByKeyRef.current[key];
      if (!row) {
        const rowEdits = { ...(prev[key] || {}) };
        rowEdits[col] = value;
        return { ...prev, [key]: rowEdits };
      }
      return stageEdit(prev, row, col, value);
    });
  }, []);

  // Stage a RESOLVED transition for the given rows once the modal supplies the
  // resolution text. Preventable isn't required to stage — it's validated
  // per-row at save (rows missing it fail there and stay staged).
  const applyResolve = useCallback((keys: string[], resolveText: string) => {
    setEdits((prev) => {
      const next = { ...prev };
      for (const key of keys) {
        next[key] = {
          ...(next[key] || {}),
          tt_status: 'RESOLVED',
          resolve: resolveText,
        };
      }
      return next;
    });
  }, []);

  const handleBulkEdit = useCallback(
    (columnId: string, selectedRows: LcResolutionItem[], value: unknown) => {
      if (!TT_FIELDS.has(columnId) && !RS_FIELDS.has(columnId) && !BOTH_FIELDS.has(columnId)) return;
      const col = columnId as EditableField;
      // Checkbox bulk edits arrive as booleans (the Reviewed column); map to Y/N.
      const v = typeof value === 'boolean' ? (value ? 'Y' : 'N') : ((value as string) ?? '');

      // Resolving via bulk collects one resolution text for all selected
      // tickets (via the modal). Preventable is NOT required up front — it's
      // validated per-row at save, so rows still missing it fail there and
      // stay staged with an error toast, while the rest save.
      if (col === 'tt_status' && v === 'RESOLVED') {
        const ttRows = selectedRows.filter((row) => row.troubleticketrecno != null);
        if (ttRows.length === 0) return;
        setResolveModal({ rowKeys: ttRows.map(rowKey), initResolve: '' });
        return;
      }

      setEdits((prev) => {
        let next: RowEdits = prev;
        for (const row of selectedRows) {
          // Only stage where the underlying record exists for this field.
          // Reviewed applies to whichever records the row carries, so any row
          // with a ticket or a service failure takes it.
          const editable = BOTH_FIELDS.has(col)
            ? row.troubleticketrecno != null || row.reschedulerecno != null
            : TT_FIELDS.has(col)
              ? row.troubleticketrecno != null
              : row.reschedulerecno != null;
          if (!editable) continue;
          // SF=Y requires a pro — skip booking tickets (pronumber 0) so bulk
          // never stages an edit the backend will reject.
          if (col === 'servicefailure' && v === 'Y' && row.pronumber === 0) continue;
          // Rows already holding the bulk value stay clean (see stageEdit).
          next = stageEdit(next, row, col, v);
        }
        return next;
      });
    },
    [],
  );

  const pendingCount = useMemo(() => Object.keys(edits).length, [edits]);

  // ── Optimistic row patches ───────────────────────────────────────────────
  // A ticket edited in the PRO drawer (or the Ticket # editor) applies to the
  // launching row immediately, so the grid doesn't sit stale until a refetch
  // lands. Each patch is superseded the moment fresh server rows arrive.
  const [optimistic, setOptimistic] = useState<Record<string, Partial<LcResolutionItem>>>({});
  useEffect(() => {
    setOptimistic((prev) => (Object.keys(prev).length ? {} : prev));
  }, [rows]);

  const displayRows = useMemo(() => {
    if (!Object.keys(optimistic).length) return rows;
    return rows.map((r) => {
      const patch = optimistic[rowKey(r)];
      return patch ? { ...r, ...patch } : r;
    });
  }, [rows, optimistic]);

  /**
   * Apply a ticket save to the row(s) it belongs to, then reconcile.
   *
   * The patch only covers columns the editor owns — a create has no row to
   * patch yet, and resolving a ticket has to DROP the row from the operational
   * queue. Both of those, plus server-set fields (datetime, userid), come back
   * with the background refetch.
   */
  // Read through refs so the listener keeps a stable identity — it's the
  // context value wrapping the PRO drawer, and re-creating it on every rows /
  // filter change would re-render that whole subtree.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const onRefetchRef = useRef(onRefetch);
  onRefetchRef.current = onRefetch;

  const handleTicketSaved = useCallback((saved?: TroubleTicketSaveResult) => {
    if (saved?.recno != null) {
      const patch: Partial<LcResolutionItem> = {
        category: saved.ticket.category,
        tt_status: saved.ticket.status,
        assignedto: saved.ticket.assignedto,
        issue: saved.ticket.issue,
        resolve: saved.ticket.resolve,
        preventable: saved.ticket.preventable,
        driverticket: saved.ticket.driverticket,
        servicefailure: saved.ticket.servicefailure,
      };
      if (saved.serviceFailure) {
        patch.servicefailuredate = saved.serviceFailure.servicefailuredate;
        patch.reason = saved.serviceFailure.reason;
        patch.servicefailuretype = saved.serviceFailure.servicefailuretype;
        patch.thd_reason = saved.serviceFailure.thd_reason;
        patch.responsibledriver = saved.serviceFailure.responsibledriver;
        patch.comments = saved.serviceFailure.comments;
      }
      setOptimistic((prev) => {
        const next = { ...prev };
        for (const r of rowsRef.current) {
          if (r.troubleticketrecno !== saved.recno) continue;
          const key = rowKey(r);
          next[key] = { ...(next[key] || {}), ...patch };
        }
        return next;
      });
    }
    onRefetchRef.current({ background: true });
  }, []);

  const rowsByKey = useMemo(() => {
    const m: Record<string, LcResolutionItem> = {};
    for (const r of displayRows) m[rowKey(r)] = r;
    return m;
  }, [displayRows]);
  rowsByKeyRef.current = rowsByKey;

  // Background auto-refresh. Pause while the user has staged edits or is
  // mid-save, and while a drawer/modal is open. useVisibilityAwareInterval also
  // pauses when the tab is hidden and fires once on resume. Disabled entirely
  // when autoRefreshMs is null (e.g. the report page).
  const autoRefreshPaused =
    pendingCount > 0 || saving || isDrawerOpen || resolveModal !== null || selectedTicket !== null || ticketLoading;
  useVisibilityAwareInterval(
    () => { onRefetch({ background: true }); },
    autoRefreshMs && !autoRefreshPaused ? autoRefreshMs : null,
  );

  const handleSave = useCallback(async () => {
    const keys = Object.keys(edits);
    if (keys.length === 0) return;
    setSaving(true);

    // A service failure (reschedule) can't be saved without a comment — the
    // same "Comments required" rule as the Service Failure modal. Hold back any
    // row that would write reschedule fields while its (effective) comment is
    // blank; valid rows still save and blocked rows stay staged so the user can
    // add a comment and retry.
    const blockedKeys: string[] = [];
    const blockedMsgs: string[] = [];

    // One operation per edited row, combining its trouble-ticket + reschedule
    // writes, so success/failure is tracked per row. The ticket write runs
    // first; if it fails the reschedule is skipped (nothing saved for that
    // row). Rows are independent — a failure in one doesn't block the others.
    const ops = keys
      .map((key) => {
        const row = rowsByKey[key];
        if (!row) return null;
        const patch = edits[key] || {};
        const label = row.pronumber
          ? `PRO ${row.pronumber}`
          : `Ticket #${row.troubleticketrecno ?? '?'}`;
        const writesReschedule =
          row.reschedulerecno != null && [...RS_FIELDS].some((f) => f in patch);
        // Reviewed rides its own path: it writes the ticket and/or the service
        // failure the row carries, but a review alone must never trip the
        // content guards (comments / type) that apply to coding an SF.
        const writesReviewed = 'reviewed' in patch;
        const reviewedFlag = (patch.reviewed === 'Y' ? 'Y' : 'N') as 'Y' | 'N';
        if (writesReschedule && !effective(row, edits, 'comments').trim()) {
          blockedKeys.push(key);
          blockedMsgs.push(`${label}: Comments are required to save a service failure`);
          return null;
        }
        // A controllable failure with no type never counts against OTD (only
        // Delivery Failures do), so it must say what failed before its
        // reschedule side can be saved. Uncontrollable reasons may stay untyped.
        if (
          writesReschedule &&
          serviceFailureTypeMissing(
            effective(row, edits, 'reason'),
            effective(row, edits, 'servicefailuretype'),
          )
        ) {
          blockedKeys.push(key);
          blockedMsgs.push(`${label}: Service Failure Type is required for a controllable (C:) reason`);
          return null;
        }
        const run = async () => {
          if (row.troubleticketrecno != null) {
            const ttPatch: TroubleTicketUpdatePayload = {};
            if ('tt_status' in patch) ttPatch.status = patch.tt_status ?? '';
            if ('assignedto' in patch) ttPatch.assignedto = patch.assignedto ?? '';
            if ('preventable' in patch) ttPatch.preventable = patch.preventable ?? '';
            if ('driverticket' in patch) ttPatch.driverticket = patch.driverticket ?? '';
            if ('servicefailure' in patch) {
              ttPatch.servicefailure = (patch.servicefailure ?? '') as 'Y' | 'N' | '';
            }
            if ('resolve' in patch) ttPatch.resolve = patch.resolve ?? '';
            if (writesReviewed) ttPatch.reviewed = reviewedFlag;
            if (Object.keys(ttPatch).length > 0) {
              await troubleTicketsService.update(row.troubleticketrecno, ttPatch);
            }
          }
          // Reschedule side. Undefined fields are omitted from the JSON body,
          // so only edited fields are sent (matches the backend exclude_unset).
          if (row.reschedulerecno != null && (writesReschedule || writesReviewed)) {
            await schedulingService.updateReschedule(row.reschedulerecno, {
              reviewed: writesReviewed ? reviewedFlag : undefined,
              // A cleared date must be sent as null (service failures are
              // allowed without a date); '' fails backend date parsing.
              servicefailuredate:
                'servicefailuredate' in patch ? (patch.servicefailuredate || null) : undefined,
              reason: patch.reason ?? undefined,
              servicefailuretype: patch.servicefailuretype ?? undefined,
              thd_reason: patch.thd_reason ?? undefined,
              responsibledriver: patch.responsibledriver ?? undefined,
              comments: patch.comments ?? undefined,
            });
          }
        };
        return { key, label, run };
      })
      .filter((o): o is { key: string; label: string; run: () => Promise<void> } => o !== null);

    const results = await Promise.allSettled(ops.map((o) => o.run()));
    setSaving(false);

    const failedKeys: string[] = [...blockedKeys];
    const failedMsgs: string[] = [...blockedMsgs];
    results.forEach((res, i) => {
      if (res.status === 'rejected') {
        failedKeys.push(ops[i].key);
        failedMsgs.push(`${ops[i].label}: ${errorMessage(res.reason)}`);
      }
    });

    // Blocked rows were never attempted (not in ops), so saved = the ops that
    // didn't reject.
    const savedCount = ops.length - (failedKeys.length - blockedKeys.length);
    // Keep the failed rows' edits staged so the user can fix and retry; clear
    // the rows that saved.
    setEdits((prev) => {
      const next: RowEdits = {};
      for (const k of failedKeys) if (prev[k]) next[k] = prev[k];
      return next;
    });

    if (failedMsgs.length > 0) {
      showToast({
        type: 'error',
        message:
          `Saved ${savedCount} row${savedCount === 1 ? '' : 's'}. ` +
          `${failedMsgs.length} did not save:\n${failedMsgs.join('\n')}`,
      });
    } else {
      showToast({
        type: 'success',
        message: `Saved ${savedCount} row${savedCount === 1 ? '' : 's'}`,
      });
    }
    onRefetch();
  }, [edits, rowsByKey, onRefetch, showToast]);

  // ── Column definitions ──────────────────────────────────────────────────
  const baseColumns = useMemo<ColumnDef<LcResolutionItem>[]>(() => {
    const editedCellClass = (key: string, col: EditableField) =>
      edits[key]?.[col] !== undefined ? 'bg-amber-50' : '';

    const cellClass = (key: string, col: EditableField) =>
      `w-full px-1 py-0.5 text-xs border border-transparent hover:border-slate-300 rounded bg-transparent ${editedCellClass(key, col)}`;

    return [
      {
        accessorKey: 'pronumber',
        header: 'PRO',
        size: 120,
        cell: (info) => {
          const pro = info.getValue() as number;
          if (!pro) return '-';
          return (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => openOrderDrawer(pro)}
                className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
              >
                {pro}
              </button>
              <a
                href={`${config.LEGACY_CMS_URL}/index_cms.pl?action=savecustomsearch&contlist=${pro}&searchtype=pronumber`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                title="Open in legacy CMS"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              </a>
            </div>
          );
        },
      },
      {
        accessorKey: 'bookingnumber',
        header: 'Booking',
        size: 120,
        // Clickable → opens the app's search results for that booking in a new
        // tab. The primary way to reach booking-only TTs (pronumber 0), which
        // carry no PRO link.
        cell: (info) => {
          const booking = info.getValue() as string | null;
          if (!booking) return null;
          return (
            <a
              href={`/searchresults?q=${encodeURIComponent(booking)}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
            >
              {booking}
            </a>
          );
        },
      },
      // order.container — the container number for this shipment.
      { accessorKey: 'container', header: 'Container', size: 120 },
      // order.bol on the row's own pro. Booking-only ticket rows carry no pro, so
      // this is blank there — their BOL is what the Booking column already shows.
      { accessorKey: 'pro_bol', header: 'Pro BOL', size: 120 },
      { accessorKey: 'customer', header: 'Customer', size: 160 },
      { accessorKey: 'pickup', header: 'Pickup', size: 150 },
      { accessorKey: 'delivery', header: 'Delivery', size: 150 },
      // order.csr — the LC the order belongs to (same value the LC filter scopes on).
      { accessorKey: 'csr', header: 'LC', size: 100 },
      // order.traceterminatedatetime — when tracing was terminated for this order.
      { accessorKey: 'traceterminatedatetime', header: 'Trace Terminate Date/Time', size: 170, meta: { isDate: true } },

      // ── Trouble-ticket side ──────────────────────────────────────────────
      { accessorKey: 'category', header: 'Category', size: 150 },
      {
        accessorKey: 'troubleticketrecno',
        header: 'Ticket #',
        size: 90,
        cell: (info) => {
          const recno = info.getValue() as number | null;
          if (recno == null) return <span className="text-gray-400">—</span>;
          return (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); void openTicketDrawer(recno); }}
              className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
            >
              {recno}
            </button>
          );
        },
      },
      {
        accessorKey: 'tt_status',
        header: 'TT Status',
        size: 120,
        meta: { bulkEdit: { type: 'dropdown', options: STATUS_OPTIONS } satisfies BulkEditMeta },
        cell: (info) => {
          const row = info.row.original;
          if (row.troubleticketrecno == null) return <span className="text-gray-400">—</span>;
          const key = rowKey(row);
          const v = effective(row, edits, 'tt_status');
          return (
            <select
              value={v}
              onChange={(e) => {
                const next = e.target.value;
                // Resolving captures resolution text now (collected via the
                // modal). Preventable is NOT required up front — it's validated
                // per-row at save, so the user can set status before
                // preventable. Rows still missing it fail at save and stay
                // staged with an error toast.
                if (next === 'RESOLVED') {
                  setResolveModal({
                    rowKeys: [key],
                    initResolve: edits[key]?.resolve ?? row.resolve ?? '',
                  });
                  return;
                }
                setRowEdit(key, 'tt_status', next);
              }}
              className={cellClass(key, 'tt_status')}
            >
              {!STATUS_OPTIONS.some((o) => o.value === v) && <option value={v}>{v || '—'}</option>}
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          );
        },
      },
      {
        accessorKey: 'assignedto',
        header: 'Assigned To',
        size: 120,
        meta: { bulkEdit: { type: 'dropdown', options: ASSIGNED_TO_OPTIONS } satisfies BulkEditMeta },
        cell: (info) => {
          const row = info.row.original;
          if (row.troubleticketrecno == null) return <span className="text-gray-400">—</span>;
          const key = rowKey(row);
          const v = effective(row, edits, 'assignedto');
          return (
            <select
              value={v}
              onChange={(e) => setRowEdit(key, 'assignedto', e.target.value)}
              className={cellClass(key, 'assignedto')}
            >
              <option value="">—</option>
              {/* Surface a stored-but-retired value (e.g. legacy WHSE CSR) so it
                  isn't silently dropped to blank. */}
              {!!v && !ASSIGNED_TO_OPTIONS.some((o) => o.value === v) && (
                <option value={v}>{v}</option>
              )}
              {ASSIGNED_TO_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          );
        },
      },
      {
        accessorKey: 'issue',
        header: 'Issue',
        size: 220,
        cell: (info) => {
          const v = info.getValue() as string | null;
          if (!v) return null;
          return (
            <Tooltip content={<div className="max-w-xs text-xs whitespace-pre-wrap">{v}</div>} placement="top">
              <span className="block truncate">{v}</span>
            </Tooltip>
          );
        },
      },
      // Editable resolution text, after Issue. Shown on both the operational
      // and report pages; inline-editable and bulk-editable like Comments.
      // Editing the text here does NOT change status — moving TT Status →
      // RESOLVED still prompts via the resolve modal, which is seeded from any
      // text already staged here.
      ...(showResolveColumn
        ? [{
            accessorKey: 'resolve',
            header: 'Resolution',
            size: 220,
            meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
            cell: (info) => {
              const row = info.row.original;
              if (row.troubleticketrecno == null) return <span className="text-gray-400">—</span>;
              const key = rowKey(row);
              const v = effective(row, edits, 'resolve');
              return (
                <TextCell
                  incoming={v}
                  className={cellClass(key, 'resolve')}
                  onCommit={(next) => setRowEdit(key, 'resolve', next)}
                />
              );
            },
          } as ColumnDef<LcResolutionItem>]
        : []),
      {
        accessorKey: 'preventable',
        header: 'Preventable',
        size: 110,
        meta: {
          bulkEdit: {
            type: 'dropdown',
            options: [{ value: 'Y', label: 'Y' }, { value: 'N', label: 'N' }],
          } satisfies BulkEditMeta,
        },
        cell: (info) => {
          const row = info.row.original;
          if (row.troubleticketrecno == null) return <span className="text-gray-400">—</span>;
          const key = rowKey(row);
          const v = effective(row, edits, 'preventable');
          return (
            <select
              value={v}
              onChange={(e) => setRowEdit(key, 'preventable', e.target.value)}
              className={cellClass(key, 'preventable')}
            >
              <option value="">—</option>
              <option value="Y">Y</option>
              <option value="N">N</option>
            </select>
          );
        },
      },
      {
        accessorKey: 'driverticket',
        header: 'Driver Ticket',
        size: 110,
        meta: {
          bulkEdit: {
            type: 'dropdown',
            options: [{ value: 'Y', label: 'Y' }, { value: 'N', label: 'N' }],
          } satisfies BulkEditMeta,
        },
        cell: (info) => {
          const row = info.row.original;
          if (row.troubleticketrecno == null) return <span className="text-gray-400">—</span>;
          const key = rowKey(row);
          const v = effective(row, edits, 'driverticket');
          // Optionally highlight the cell red when this is a driver ticket (Y).
          // `!` so the red wins over cellClass's bg-transparent / edited-amber.
          const driverYClass =
            highlightDriverTicketY && v === 'Y'
              ? '!bg-red-100 dark:!bg-red-900/40 !text-red-700 dark:!text-red-300 font-semibold'
              : '';
          return (
            <select
              value={v}
              onChange={(e) => setRowEdit(key, 'driverticket', e.target.value)}
              className={`${cellClass(key, 'driverticket')} ${driverYClass}`}
            >
              <option value="Y">Y</option>
              <option value="N">N</option>
            </select>
          );
        },
      },
      {
        accessorKey: 'servicefailure',
        header: 'SF Flag',
        size: 90,
        // Y is settable here for pro tickets — on save the backend inserts a
        // blank linked reschedule (which then appears on this row for the user
        // to fill in). Booking-only tickets (pronumber 0) can't be flagged Y: a
        // service failure must be tied to a pro.
        meta: {
          bulkEdit: {
            type: 'dropdown',
            options: [{ value: '', label: '—' }, { value: 'Y', label: 'Y' }, { value: 'N', label: 'N' }],
          } satisfies BulkEditMeta,
        },
        cell: (info) => {
          const row = info.row.original;
          if (row.troubleticketrecno == null) return <span className="text-gray-400">—</span>;
          const key = rowKey(row);
          const v = effective(row, edits, 'servicefailure');
          // Y requires a pro. Offer it for pro tickets; for booking tickets
          // (pronumber 0) only when already saved as Y (legacy), so the row can
          // be moved off Y but never onto it.
          const savedAsY = (row.servicefailure ?? '') === 'Y';
          const allowY = row.pronumber !== 0 || savedAsY;
          return (
            <select
              value={v}
              onChange={(e) => setRowEdit(key, 'servicefailure', e.target.value)}
              className={cellClass(key, 'servicefailure')}
            >
              {allowY && <option value="Y">Y</option>}
              <option value="">—</option>
              <option value="N">N</option>
            </select>
          );
        },
      },
      { accessorKey: 'tt_userid', header: 'TT User', size: 120 },
      { accessorKey: 'tt_datetime', header: 'TT Date', size: 150, meta: { isDate: true } },

      // ── Reschedule side ──────────────────────────────────────────────────
      // registryTitle: false — these two are the RESCHEDULE's author and date, not the
      // order's `createdby` / `createdatetime` their ids collide with, so they must not
      // be re-titled from those definitions.
      { accessorKey: 'createdby', header: 'User', size: 120, meta: { registryTitle: false } },
      { accessorKey: 'coded_by', header: 'Coded By', size: 120 },
      { accessorKey: 'createdatetime', header: 'Create Date', size: 150, meta: { isDate: true, registryTitle: false } },
      {
        accessorKey: 'servicefailuredate',
        header: 'Service Failure Date',
        size: 150,
        // Intentionally NOT setting meta.isDate — that flag short-circuits the
        // cell renderer with a formatted string, skipping the DateTimePicker.
        meta: { bulkEdit: { type: 'date' } satisfies BulkEditMeta },
        cell: (info) => {
          const row = info.row.original;
          if (row.reschedulerecno == null) return <span className="text-gray-400">—</span>;
          const key = rowKey(row);
          const v = effective(row, edits, 'servicefailuredate');
          // Parse 'YYYY-MM-DD' as a local-tz date so the calendar shows the
          // same day the user typed (Date(iso) parses as UTC and can drift).
          const dateValue = v
            ? (() => {
                const [y, m, d] = v.split('-').map(Number);
                return Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d)
                  ? new Date(y, m - 1, d)
                  : null;
              })()
            : null;
          return (
            <div className={`w-full ${editedCellClass(key, 'servicefailuredate')}`}>
              <DateTimePicker
                mode="date"
                selectionType="single"
                inputVariant="compact"
                usePortal
                autoApply
                labelType="none"
                value={dateValue}
                onChange={(val) => {
                  const d = val instanceof Date ? val : null;
                  const iso = d
                    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
                    : '';
                  setRowEdit(key, 'servicefailuredate', iso);
                }}
              />
            </div>
          );
        },
      },
      // reschedule.olddate — the appointment date the service failure moved OFF
      // of (read-only here; it is set when the reschedule is recorded). Hidden by
      // default — see defaultColumnVisibility on the TanTable below.
      { accessorKey: 'olddate', header: 'Old Date', size: 110, meta: { isDate: true } },
      {
        accessorKey: 'reason',
        header: 'Reason',
        size: 200,
        meta: {
          bulkEdit: {
            type: 'dropdown',
            // No '(blank)' option — once a reason is coded the backend rejects
            // clearing it, and bulk-setting blank on uncoded rows is a no-op.
            options: reasonOptions.map((r) => ({ value: r, label: r })),
          } satisfies BulkEditMeta,
        },
        cell: (info) => {
          const row = info.row.original;
          if (row.reschedulerecno == null) return <span className="text-gray-400">—</span>;
          const key = rowKey(row);
          const v = effective(row, edits, 'reason');
          // Once the saved reason is non-blank it can be changed but not cleared
          // (the backend rejects blank), so suppress the clear button.
          const reasonCoded = !!(row.reason ?? '').trim();
          return (
            <div className={editedCellClass(key, 'reason')}>
              <TypeAhead
                options={reasonOptions.map((r) => ({ value: r, label: r }))}
                value={v}
                onChange={(next) => setRowEdit(key, 'reason', next)}
                onClear={() => setRowEdit(key, 'reason', '')}
                placeholder="Reason…"
                compact
                showSearch
                allowClear={!reasonCoded}
                flagInvalidValue
              />
            </div>
          );
        },
      },
      {
        // reschedule.servicefailuretype — what failed. Only Delivery Failures
        // count against OTD, so a controllable (C:) reason can't be saved
        // without one (handleSave); uncontrollable rows may leave it blank.
        accessorKey: 'servicefailuretype',
        header: 'SF Type',
        size: 170,
        meta: {
          bulkEdit: {
            type: 'dropdown',
            options: [
              { value: '', label: '(blank)' },
              ...SERVICE_FAILURE_TYPE_OPTIONS,
            ],
          } satisfies BulkEditMeta,
        },
        cell: (info) => {
          const row = info.row.original;
          if (row.reschedulerecno == null) return <span className="text-gray-400">—</span>;
          const key = rowKey(row);
          const v = effective(row, edits, 'servicefailuretype');
          return (
            <div className={editedCellClass(key, 'servicefailuretype')}>
              <TypeAhead
                options={SERVICE_FAILURE_TYPE_OPTIONS}
                value={v}
                onChange={(next) => setRowEdit(key, 'servicefailuretype', next)}
                onClear={() => setRowEdit(key, 'servicefailuretype', '')}
                placeholder="Type…"
                compact
                showSearch
                showDescriptions
                flagInvalidValue
              />
            </div>
          );
        },
      },
      {
        accessorKey: 'thd_reason',
        header: 'THD Reason',
        size: 200,
        meta: {
          bulkEdit: {
            type: 'dropdown',
            options: [
              { value: '', label: '(blank)' },
              ...thdReasonOptions.map((r) => ({ value: r, label: r })),
            ],
          } satisfies BulkEditMeta,
        },
        cell: (info) => {
          const row = info.row.original;
          if (row.reschedulerecno == null) return <span className="text-gray-400">—</span>;
          const key = rowKey(row);
          const v = effective(row, edits, 'thd_reason');
          return (
            <div className={editedCellClass(key, 'thd_reason')}>
              <TypeAhead
                options={thdReasonOptions.map((r) => ({ value: r, label: r }))}
                value={v}
                onChange={(next) => setRowEdit(key, 'thd_reason', next)}
                onClear={() => setRowEdit(key, 'thd_reason', '')}
                placeholder="THD Reason…"
                compact
                showSearch
                flagInvalidValue
              />
            </div>
          );
        },
      },
      {
        accessorKey: 'responsibledriver',
        header: 'Driver',
        size: 170,
        meta: { bulkEdit: { type: 'dropdown', options: driverBulkOptions } satisfies BulkEditMeta },
        cell: (info) => {
          const row = info.row.original;
          if (row.reschedulerecno == null) return <span className="text-gray-400">—</span>;
          const key = rowKey(row);
          const v = effective(row, edits, 'responsibledriver');
          return (
            <div className={editedCellClass(key, 'responsibledriver')}>
              <TypeAhead
                options={driverOptions}
                value={v}
                onChange={(next) => setRowEdit(key, 'responsibledriver', next)}
                onClear={() => setRowEdit(key, 'responsibledriver', '')}
                placeholder="Driver…"
                compact
                showSearch
                showDescriptions
                groupBy
                flagInvalidValue
              />
            </div>
          );
        },
      },
      {
        accessorKey: 'comments',
        header: 'Comments',
        size: 220,
        meta: { bulkEdit: { type: 'text' } satisfies BulkEditMeta },
        cell: (info) => {
          const row = info.row.original;
          if (row.reschedulerecno == null) return <span className="text-gray-400">—</span>;
          const key = rowKey(row);
          const v = effective(row, edits, 'comments');
          return (
            <TextCell
              incoming={v}
              className={cellClass(key, 'comments')}
              maxLength={2000}
              onCommit={(next) => setRowEdit(key, 'comments', next)}
            />
          );
        },
      },

      // ── Review (report page only) ────────────────────────────────────────
      // One checkbox per row, derived by the backend from reviewedby (Y only
      // when every record present carries one) alongside the more recent
      // reviewer / time. Checking stamps, unchecking clears, on whichever
      // records the row carries — the server writes who / when.
      ...(showReviewColumns
        ? ([
            {
              accessorKey: 'reviewed',
              header: 'Reviewed',
              size: 90,
              meta: { bulkEdit: { type: 'checkbox' } satisfies BulkEditMeta },
              cell: (info) => {
                const row = info.row.original;
                if (row.troubleticketrecno == null && row.reschedulerecno == null) {
                  return <span className="text-gray-400">—</span>;
                }
                const key = rowKey(row);
                const checked = effective(row, edits, 'reviewed') === 'Y';
                return (
                  <div className={`flex justify-center ${editedCellClass(key, 'reviewed')}`}>
                    <input
                      type="checkbox"
                      aria-label="Reviewed"
                      checked={checked}
                      onChange={(e) => setRowEdit(key, 'reviewed', e.target.checked ? 'Y' : 'N')}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />
                  </div>
                );
              },
            },
            { accessorKey: 'reviewedby', header: 'Reviewed By', size: 130 },
            { accessorKey: 'revieweddatetime', header: 'Reviewed Date/Time', size: 160, meta: { isDate: true } },
          ] as ColumnDef<LcResolutionItem>[])
        : []),
    ];
  }, [edits, reasonOptions, thdReasonOptions, driverOptions, driverBulkOptions, highlightDriverTicketY, showResolveColumn, showReviewColumns, openOrderDrawer, openTicketDrawer, setRowEdit]);

  // Any orders-scope registry field can be added as a column. Rows are work ITEMS —
  // a pro with two open tickets is two rows — so an added order column repeats down
  // them; they are sibling rows, not a parent with sub-rows, so no isSubRow.
  const { columns, availableFields } = useAdditionalFieldColumns<LcResolutionItem>(
    fieldSelection,
    { baseColumns, coveredFields: LC_RESOLUTION_COVERED_FIELDS },
  );

  return (
    <div className="flex flex-col h-full min-h-0 p-4 gap-3">
      {/* Header row: title + Save */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-heading">{title}</h1>
          <p className="text-xs text-muted">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          {pendingCount > 0 && (
            <button
              type="button"
              onClick={() => setEdits({})}
              disabled={saving}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50 dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600"
            >
              Discard
            </button>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={pendingCount === 0 || saving}
            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : pendingCount > 0 ? `Save ${pendingCount} row${pendingCount === 1 ? '' : 's'}` : 'Save'}
          </button>
        </div>
      </div>

      {/* Page-specific filters */}
      {filterBar && <div className="flex items-center gap-3 flex-wrap">{filterBar}</div>}

      {/* Table */}
      <div className="flex-1 min-h-0">
        <TanTable
          data={displayRows}
          columns={columns}
          availableFields={availableFields}
          additionalFields={fieldSelection.additionalFields}
          onAdditionalFieldsChange={fieldSelection.onAdditionalFieldsChange}
          loading={loading}
          getRowId={rowKey}
          settingsKey={settingsKey}
          cardTitle={title}
          searchPlaceholder="Search TTs & service failures..."
          fillContainer
          enableSearch
          enableReset
          enableShowHideCols
          enableColumnFilters
          {...(listFilterScopeNote ? { listFilterScopeNote } : {})}
          enableGrouping
          // Curated to the categorical columns worth grouping on; PRO/Booking/
          // Ticket #/dates/comments are per-row unique or free-text, so they're
          // left out of the Group By menu.
          groupableColumns={[
            'customer',
            'csr',
            'category',
            'pickup',
            'delivery',
            'tt_status',
            'assignedto',
            'preventable',
            'coded_by',
            'reason',
            'servicefailuretype',
            'responsibledriver',
            ...(showReviewColumns ? ['reviewed', 'reviewedby'] : []),
          ]}
          enableZebraStriping={false}
          // Old Date is offered, not imposed: both pages already carry saved
          // per-user layouts, so a new column that forced itself onto every one
          // of them would be a change nobody asked for. Hidden here means it
          // shows up in the "+ Add column" picker (and in Show/Hide) for anyone
          // who wants it, and stays off after Reset To Defaults.
          defaultColumnVisibility={{ olddate: false }}
          // Click-to-highlight so users can see which rows they've already
          // worked: PRO in green, Ticket # in light blue. The two are tracked
          // independently — each keeps its own last-clicked ring, and a PRO and
          // a ticket that happen to share digits never highlight together.
          trackViewedColumnId="pronumber"
          trackViewedBlueColumnId="troubleticketrecno"
          // No client default sort: the backend returns rows interleaved
          // chronologically by COALESCE(tt_datetime, createdatetime). Sorting by
          // troubleticketrecno here clumped service failures (no TT recno) apart.
          defaultSorting={[]}
          onBulkEdit={handleBulkEdit}
        />
      </div>

      {/* Order drawer. Wrapped in the ticket-save provider so a trouble ticket
          edited on the drawer's Trouble Tickets sub-tab patches the row it was
          launched from — the editor is five levels down and reaches us through
          context rather than a drilled callback. */}
      <TroubleTicketSaveProvider onTicketSaved={handleTicketSaved}>
        <ProDetailSlideOver
          pronumber={selectedPro}
          isOpen={isDrawerOpen && selectedPro !== null}
          onClose={closeOrderDrawer}
        />

        {/* Trouble ticket editor — right-side drawer, same as the Dispatch
            Dashboard. Saving may change a ticket's status (e.g. resolve), which
            affects the queue; the provider patches the row, then reconciles in
            the background. */}
        {selectedTicket && (
          <TroubleTicketEditModal
            variant="drawer"
            pronumber={selectedTicket.pronumber}
            ticket={selectedTicket}
            billTo={selectedTicket.billto ?? undefined}
            onOpenPro={openOrderDrawer}
            onClose={() => setSelectedTicket(null)}
            onSaved={() => { setSelectedTicket(null); }}
          />
        )}
      </TroubleTicketSaveProvider>

      {/* Resolve modal — captures preventable + resolution text before a
          ticket (or several, via bulk) can move to RESOLVED. */}
      {resolveModal && (
        <ResolveTicketModal
          count={resolveModal.rowKeys.length}
          initialResolve={resolveModal.initResolve}
          onCancel={() => setResolveModal(null)}
          onConfirm={(resolveText) => {
            applyResolve(resolveModal.rowKeys, resolveText);
            setResolveModal(null);
          }}
        />
      )}
    </div>
  );
};

/** Provides the page-wide viewed-value state the PRO (green) and Ticket # (light
 *  blue) highlights read from. */
const LcResolutionBoard: React.FC<LcResolutionBoardProps> = (props) => (
  <ViewedProsProvider>
    <LcResolutionBoardContent {...props} />
  </ViewedProsProvider>
);

export default LcResolutionBoard;
